/**
 * Die **Zustellung** (#102, #104; ADR-0027): die zwei Wege, auf denen der
 * Übergabetext das Haus verlässt — als Kommentar an einem bestehenden Vorgang im
 * Ticketsystem und als Direktnachricht an einen Menschen in Mattermost.
 *
 * Sie steht hier und nicht in einer Ansicht, weil sie an **zwei** Stellen
 * auftaucht und beide Male dasselbe meinen muss: im Übergabe-Dialog als Knopf
 * und in den Listen als leise Marke.
 *
 * Vier Festlegungen tragen sie:
 *
 * - **Ein Dialog, mehrere Wege, und je Weg ein Knopf.** Die Knöpfe sitzen
 *   neben „Kopieren" und nicht in einer dritten Spalte der ohnehin breiten
 *   Tabelle. Ein Knopf bedient genau einen Weg — ein Knopf mit einer Auswahl
 *   daneben wäre eine Zustellung, bei der sich der Weg im letzten Moment ändern
 *   lässt. Der Kopierweg bleibt daneben stehen: Er ist der Rückfall, der immer
 *   funktioniert.
 * - **Gefragt wird beim Öffnen, und dort je Verweis genau einmal.** Beim
 *   Anzeigen einer Liste ergeht keine Anfrage — eine Revisionsliste mit dreißig
 *   Zeilen löste sonst dreißig Anfragen an eine fremde Gegenstelle aus.
 * - **Kein Fall bleibt still.** Kein Verweis, Nummer gibt es nicht, Zugriff
 *   abgelehnt: Jedes Mal steht der Grund im Klartext daneben. Ein Knopf, der
 *   ohne Erklärung fehlt oder grau ist, erzeugt genau die Rückfrage, die er
 *   ersparen soll.
 * - **Die Vorschau ist die Bestätigung.** Es gibt keinen zweiten
 *   Rückfragedialog: Wer den Text gelesen hat und den Knopf drückt, hat
 *   entschieden.
 *
 * **Die Marke ist kein Nachhalten** (ADR-0012). Sie sagt „darüber wurde
 * geredet", nicht „erledigt" — und trägt deshalb ein anderes Zeichen (`send`
 * statt `flag`) und einen anderen Wortlaut.
 */

import { useEffect, useState } from "react";
import type { ReactElement } from "react";

import { Button, Icon } from "../design-system";
import type {
  ChatRecipient,
  Delivered,
  DeliveryApi,
  DeliveryCut,
  DeliveryPreparation,
  DeliveryTarget,
} from "../api/client";
import { formatAge, formatRelative } from "../formatting";
import { Notice, describe } from "./loading";

/**
 * Was auf dem Knopf steht. „Ticket" und nicht „Vorgang": Der Mensch will wissen, wo
 * der Text landet.
 *
 * Steht ein Ziel fest, tritt an die Stelle des nackten Worts das **volle Ziel**
 * `owner/repo#nummer` (#112) — ein Kommentar kann in zwei verschiedene
 * Ticketlisten gehen, und zweimal „435" aus zwei Listen sind zwei verschiedene
 * Vorgänge. Der Titel des Vorgangs steht daneben am Knopf und in der Auswahl:
 * Niemand soll blind in ein fremdes Ticket schreiben.
 */
const DELIVER = "Ins Ticket kommentieren";

/**
 * Was auf dem zweiten Knopf steht.
 *
 * „Mattermost" und nicht „Chat": Der Bruch zur herstellerneutralen Innenseite ist
 * gewollt (ADR-0027). Ein Mensch, der einen Text abschickt, will wissen, in
 * welchem Fenster er die Antwort erwarten soll und wer sie sieht — „Chat"
 * beantwortet das nicht.
 */
const MESSAGE = "Als Mattermost-Nachricht senden";

/** Woraus der Empfänger gewählt wird — die Beschriftung der Auswahl. */
const RECIPIENT_LABEL = "Empfänger der Direktnachricht";

/**
 * Was ein zweites Mal wäre — je Weg das, was dort tatsächlich entsteht.
 *
 * „Ein zweiter Kommentar" neben einer Direktnachricht wäre falsch, und
 * „Zustellung" wäre die Abstraktion, die dem Menschen nichts sagt. Vor dem ersten
 * Mal steht dagegen der neutrale Satz: Da ist der Weg noch nicht gewählt.
 */
const SECOND_COMMENT = "es wird ein zweiter Kommentar.";

const SECOND_MESSAGE = "es wird eine zweite Nachricht.";

const PREFACE_LABEL = "Vorwort (freiwillig)";

const PREFACE_HINT = "Ein eigener Satz darüber, etwa: schau mal, eilt nicht.";

/** Was der Vermerk in der Liste sagt — ausdrücklich nicht „erledigt". */
const MARK = "Schon zugestellt";

/**
 * Die **leise Marke** an einer Zeile: Dieser Zuschnitt war schon einmal draußen.
 *
 * Sie steht mit ihrem Zeitabstand da, weil „schon zugestellt" ohne ihn nicht
 * sagt, ob das vor zwei Stunden oder vor drei Monaten war — und danach
 * entscheidet sich, ob noch einmal zugestellt wird.
 *
 * **Sichtbar steht der Abstand kurz, vorgelesen wird er ganz.** In der Zeile ist
 * `1 Std` die zweite Zeitangabe neben dem Zeitpunkt der Übergabe, und beide sind
 * gleich geschrieben (`formatAge`); im `title` und im `aria-label` steht
 * weiterhin der ganze Satz „Schon zugestellt, vor 1 Stunde" — ein Vorleser
 * bekommt keinen Bruchteil.
 *
 * `null` ergibt gar nichts: Eine Marke, die auch „nie" bedeuten kann, ist keine.
 */
export function DeliveryMark({ at }: { at: string | null }): ReactElement | null {
  if (at === null) {
    return null;
  }

  const label = `${MARK}, ${formatRelative(at)}`;

  return (
    <span
      title={label}
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--sp-3)",
        color: "var(--fg-6)",
        fontSize: "var(--fs-11)",
      }}
    >
      <Icon name="send" size={12} />
      {formatAge(at)}
    </span>
  );
}

/**
 * Was der Zustellteil des Dialogs hergibt: **je Weg einen Knopf** und die Fläche
 * darüber.
 *
 * Zwei Felder und nicht eine Liste: Der Dialog setzt sie nebeneinander, und wo
 * welcher steht, entscheidet er — nicht diese Reihenfolge.
 */
export interface Delivery {
  ticketButton: ReactElement | null;
  chatButton: ReactElement | null;
  panel: ReactElement | null;
}

/**
 * Der Zustand der Zustellung im offenen Dialog.
 *
 * Er fragt beim Erscheinen **einmal** nach — genau das ist die Zusage — und
 * hält danach das Vorwort, die Wahl des Ziels und den Ausgang des letzten
 * Versuchs.
 *
 * `cut === null` heißt: Zu diesem Zuschnitt gibt es keine Zustellung. Das kommt
 * bei einer Stelle aus dem Bestandslauf vor, zu der es keine Revision gibt —
 * ohne Revision gibt es keinen Commit-Kommentar und damit keinen Ticketverweis.
 */
/** Was nach einer gelungenen Zustellung dasteht: wohin, wann, und was ein zweites Mal wäre. */
interface Sent {
  target: string;
  at: string;
  again: string;
}

/**
 * Der Zustand der Zustellung im offenen Dialog.
 *
 * Er fragt beim Erscheinen **einmal** nach — genau das ist die Zusage — und
 * hält danach das Vorwort, die Wahl von Ziel und Empfänger und den Ausgang des
 * letzten Versuchs.
 *
 * **Ein Zustand für beide Wege, aber zwei Wahlen.** Vorwort und Ausgang sind
 * gemeinsam, denn gedrückt wird einer der Knöpfe und nicht beide; Ziel und
 * Empfänger sind getrennt, weil sie verschiedene Dinge bezeichnen.
 *
 * `cut === null` heißt: Zu diesem Zuschnitt gibt es keine Zustellung. Das kommt
 * bei einer Stelle aus dem Bestandslauf vor, zu der es keine Revision gibt —
 * ohne Revision gibt es keinen Commit-Kommentar und damit keinen Ticketverweis.
 */
export function useDelivery(
  client: DeliveryApi | null,
  cut: DeliveryCut | null,
): Delivery {
  const [state, setState] = useState<DeliveryPreparation | null>(null);
  const [complaint, setComplaint] = useState<string | null>(null);
  const [preface, setPreface] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [chosenPerson, setChosenPerson] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Sent | null>(null);

  // Die Frage steht als Zeichenkette in der Abhängigkeit: Der Zuschnitt entsteht
  // bei jedem Rendern neu, und ein neues Objekt löste jedes Mal eine neue
  // Anfrage aus — also genau die dreißig Anfragen, die hier vermieden werden.
  const asked = cut === null ? null : JSON.stringify(cut);

  useEffect(() => {
    if (client === null || cut === null) {
      return;
    }

    let discarded = false;

    client.deliveryPreparation(cut).then(
      (value) => {
        if (!discarded) {
          setState(value);
        }
      },
      (error: unknown) => {
        if (!discarded) {
          setComplaint(describe(error));
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [client, cut, asked]);

  if (client === null || cut === null) {
    return { ticketButton: null, chatButton: null, panel: null };
  }

  const targets = state?.targets ?? [];
  // **Nie stillschweigend der erste.** Bei genau einem Ziel gibt es nichts zu
  // wählen — die Auswahl wäre eine Frage mit einer Antwort. Bei mehreren wählt
  // der Mensch, und bis dahin ist der Knopf gesperrt.
  const target = targets.length === 1 ? targets[0] : targets.find((one) => one.target === chosen);

  const recipients = state?.chat.recipients ?? [];
  // **Vorbelegt, aber änderbar**: Die Wahl des Menschen schlägt den Vorschlag des
  // Servers, und ohne Vorschlag bleibt der Knopf gesperrt, bis gewählt ist. Ein
  // fester Empfänger baute die erste Umgehung am Tag eins ein.
  const chosenRecipient = chosenPerson ?? state?.chat.suggested ?? null;
  const recipient = recipients.find((one) => one.personId === chosenRecipient);

  const deliveredAt = sent?.at ?? state?.deliveredAt ?? null;

  const send = (attempt: Promise<Delivered>, again: string): void => {
    setSending(true);
    setComplaint(null);

    attempt.then(
      (delivered) => {
        setSending(false);
        setSent({ target: delivered.target, at: delivered.at, again });
      },
      (error: unknown) => {
        // Der Text bleibt stehen und ist weiter kopierbar: Der Kopierweg ist der
        // Rückfall, der immer funktioniert (ADR-0027).
        setSending(false);
        setComplaint(describe(error));
      },
    );
  };

  return {
    ticketButton:
      // Ohne Ziel gibt es keinen Knopf — dafür steht der Grund im Klartext in
      // der Fläche darüber. Gesperrt wird er dagegen, wo es mehrere Ziele gibt
      // und noch keines gewählt ist: Verborgen sähe das aus, als gäbe es den
      // Weg nie.
      targets.length === 0 ? null : (
        <Button
          variant="primary"
          icon="send"
          disabled={sending || target === undefined}
          title={
            target === undefined ? DELIVER : `${DELIVER}: ${target.target} · ${target.title}`
          }
          onClick={() => {
            if (target !== undefined) {
              send(client.deliver(cut, target, preface), SECOND_COMMENT);
            }
          }}
        >
          {target === undefined ? DELIVER : `Ins Ticket ${target.target} kommentieren`}
        </Button>
      ),
    chatButton:
      // Dieselbe Regel wie beim Ticketweg: Kein Empfänger heißt kein Knopf, und
      // der Grund steht darüber. Betont wird höchstens **einer** der beiden Wege
      // — zwei gleich laute Knöpfe wären eine Frage, die keiner von beiden
      // stellt, und keiner der Wege ist der richtigere.
      recipients.length === 0 ? null : (
        <Button
          variant={targets.length === 0 ? "primary" : "outline"}
          icon="send"
          disabled={sending || recipient === undefined}
          title={recipient === undefined ? MESSAGE : `${MESSAGE}: ${recipient.displayName}`}
          onClick={() => {
            if (recipient !== undefined) {
              send(client.deliverToChat(cut, recipient, preface), SECOND_MESSAGE);
            }
          }}
        >
          {recipient === undefined
            ? MESSAGE
            : `Als Mattermost-Nachricht an ${recipient.displayName} senden`}
        </Button>
      ),
    panel: (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
        {state === null && complaint === null ? (
          <Notice text="Ziele werden ermittelt ..." glyph="timer" />
        ) : null}

        {state?.obstacle === undefined || state.obstacle === null ? null : (
          <Notice text={state.obstacle} glyph="circle-alert" />
        )}

        {/*
          Der Grund des Chatwegs steht neben dem des Ticketwegs und nicht an
          seiner Stelle: Es sind zwei Wege, und dass der eine fehlt, sagt über den
          anderen nichts.
        */}
        {state?.chat.obstacle === undefined || state.chat.obstacle === null ? null : (
          <Notice text={state.chat.obstacle} glyph="circle-alert" />
        )}

        {/*
          Zwei Sätze für zwei Lagen, und keiner davon ist eine Rückfrage: Nach
          dem Drücken sagt die Fläche, **wohin** der Text ging — vorher, dass er
          dort schon einmal war. Verboten ist das zweite Mal nicht: Nachfassen
          ist ein legitimer Vorgang, und wer es verböte, schickte den Menschen
          zurück ins Abschreiben von Hand.
        */}
        {sent !== null ? (
          <Notice
            text={`Zugestellt an ${sent.target}. Noch einmal zuzustellen ist möglich — ${sent.again}`}
            glyph="check"
          />
        ) : deliveredAt === null ? null : (
          <Notice
            text={
              `Dieser Text war schon einmal draußen, ${formatRelative(deliveredAt)}. ` +
              "Noch einmal zuzustellen ist möglich — es entsteht eine zweite Zustellung."
            }
            glyph="send"
          />
        )}

        {complaint === null ? null : (
          <Notice text={complaint} glyph="unplug" color="var(--ac-red)" />
        )}

        {targets.length === 0 ? null : (
          <Targets targets={targets} chosen={target?.target ?? null} onChoose={setChosen} />
        )}

        {recipients.length === 0 ? null : (
          <Recipients
            recipients={recipients}
            chosen={recipient?.personId ?? null}
            onChoose={setChosenPerson}
          />
        )}

        {targets.length === 0 && recipients.length === 0 ? null : (
          <label style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              {PREFACE_LABEL}
            </span>
            <textarea
              aria-label={PREFACE_LABEL}
              rows={2}
              value={preface}
              placeholder={PREFACE_HINT}
              onChange={(event) => setPreface(event.target.value)}
              className="erp-scroll"
              style={{
                width: "100%",
                padding: "var(--sp-3) var(--sp-4)",
                background: "var(--surface-field)",
                border: "1px solid var(--border-field)",
                borderRadius: "var(--r-field)",
                color: "var(--fg-1)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--fs-12)",
                lineHeight: "var(--lh-normal)",
                resize: "vertical",
                outline: "none",
              }}
            />
          </label>
        )}
      </div>
    ),
  };
}

/**
 * Die **Empfänger** zur Wahl — die Personen dieses Systems, die eine
 * Mattermost-Kennung tragen.
 *
 * **Immer als Auswahl, auch bei einer Person.** Anders als beim Ticketziel ist
 * hier eine Vorbelegung im Spiel: Sie muss sichtbar änderbar sein, und wo keine
 * greift, muss überhaupt etwas zu wählen sein — sonst bliebe der Knopf gesperrt,
 * und der Weg wäre dicht, ohne dass es jemand sagt.
 *
 * **Die Mattermost-Kennung steht nicht dabei** (ADR-0027): Sie ist die Kennung an
 * einer zweiten Gegenstelle und steht nur im Verwaltungsdialog. Wohin der Text
 * geht, sagt der Anzeigename.
 */
function Recipients({
  recipients,
  chosen,
  onChoose,
}: {
  recipients: ChatRecipient[];
  chosen: number | null;
  onChoose: (personId: number) => void;
}): ReactElement {
  return (
    <div
      role="radiogroup"
      aria-label={RECIPIENT_LABEL}
      style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
    >
      <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
        {RECIPIENT_LABEL}
      </span>
      {recipients.map((recipient) => (
        <label
          key={recipient.personId}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--sp-4)",
            fontSize: "var(--fs-12)",
            color: "var(--text-body)",
          }}
        >
          <input
            type="radio"
            name="delivery-recipient"
            value={String(recipient.personId)}
            checked={chosen === recipient.personId}
            onChange={() => onChoose(recipient.personId)}
          />
          <span style={{ color: "var(--fg-1)" }}>{recipient.displayName}</span>
        </label>
      ))}
    </div>
  );
}

function Targets({
  targets,
  chosen,
  onChoose,
}: {
  targets: DeliveryTarget[];
  chosen: string | null;
  onChoose: (target: string) => void;
}): ReactElement {
  const single = targets.length === 1;

  return (
    <div
      role={single ? undefined : "radiogroup"}
      aria-label="Ziel der Zustellung"
      style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
    >
      {targets.map((target) => (
        <label
          key={target.target}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--sp-4)",
            fontSize: "var(--fs-12)",
            color: "var(--text-body)",
          }}
        >
          {single ? null : (
            <input
              type="radio"
              name="delivery-target"
              value={target.target}
              checked={chosen === target.target}
              onChange={() => onChoose(target.target)}
            />
          )}
          <span style={{ color: "var(--fg-1)" }}>{target.target}</span>
          <span>{target.title}</span>
          {target.open ? null : (
            // Kommentieren ist erlaubt — der Dialog sagt es vorher. Ein Befund zu
            // einem abgeschlossenen Vorgang ist oft genau die Information, die
            // dort fehlt (ADR-0027).
            <span style={{ color: "var(--ac-amber)", fontSize: "var(--fs-11)" }}>
              geschlossen — kommentieren ist trotzdem möglich
            </span>
          )}
        </label>
      ))}
    </div>
  );
}
